import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const rupiah = (n) => `Rp ${Number(n || 0).toLocaleString("id-ID")}`;
const MONTH_LABEL = (m) => new Date(`${m}-02`).toLocaleDateString("id-ID", { month: "long", year: "numeric" });

export default function FundSupportTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [redeemForm, setRedeemForm] = useState(null);
    const [unpaidMonths, setUnpaidMonths] = useState([]);
    const [redeeming, setRedeeming] = useState(false);
    const [syncing, setSyncing] = useState(false);

    const syncPending = async () => {
        setSyncing(true);
        try {
            const { data: r } = await api.post("/finance/funds/sync-pending");
            toast.success(r.recovered > 0
                ? `Sinkronisasi selesai: ${r.recovered} order lunas berhasil dipulihkan dari ${r.checked} order pending`
                : `Sinkronisasi selesai: ${r.checked} order pending diperiksa, tidak ada yang sudah lunas`);
            if (r.recovered > 0) load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSyncing(false);
        }
    };

    const load = async () => {
        setLoading(true);
        try {
            const { data: d } = await api.get("/finance/funds");
            setData(d);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const openRedeem = async (row) => {
        setRedeemForm({ athlete_id: row.athlete_id, name: row.name, balance: row.balance, kind: "dues", amount: row.balance, period_month: "" });
        setUnpaidMonths([]);
        try {
            const { data: d } = await api.get(`/finance/athletes/${row.student_id}/unpaid-months`);
            setUnpaidMonths(d.months || []);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const fee = redeemForm ? (redeemForm.kind === "dues" ? (data?.dues_fee ?? 10000) : Math.round(Number(redeemForm.amount || 0) * (data?.cash_rate ?? 0.12))) : 0;
    const net = redeemForm ? Math.max(0, Number(redeemForm.amount || 0) - fee) : 0;

    const submitRedeem = async () => {
        const amount = Number(redeemForm.amount || 0);
        if (amount <= 0 || amount > redeemForm.balance) {
            toast.error(`Jumlah redeem harus antara Rp 1 sampai saldo ${rupiah(redeemForm.balance)}`);
            return;
        }
        if (redeemForm.kind === "dues" && !redeemForm.period_month) {
            toast.error("Pilih bulan iuran tujuan");
            return;
        }
        setRedeeming(true);
        try {
            const { data: r } = await api.post("/finance/funds/redeem", {
                athlete_id: redeemForm.athlete_id,
                kind: redeemForm.kind,
                amount,
                period_month: redeemForm.period_month,
            });
            toast.success(redeemForm.kind === "dues" ? `Redeem tercatat: ${rupiah(r.net)} masuk iuran ${MONTH_LABEL(r.period_month)} (kwitansi ${r.receipt_no})` : `Redeem tunai tercatat: ${rupiah(r.net)} diserahkan`);
            setRedeemForm(null);
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setRedeeming(false);
        }
    };

    return (
        <div data-testid="funds-tab">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <h2 className="font-display uppercase text-2xl">Uang Latihan Atlet</h2>
                    <p className="text-sm text-racing-muted mt-2">Dukungan uang latihan dari publik via pembayaran online. Redeem ke iuran bulanan (potongan Rp 10.000) atau uang tunai (potongan admin 12%).</p>
                </div>
                <button onClick={syncPending} disabled={syncing} data-testid="funds-sync-pending" className="shrink-0 border border-racing-red text-racing-red hover:bg-racing-red hover:text-white disabled:opacity-50 text-[10px] uppercase tracking-[0.15em] px-4 py-3 transition-colors">
                    {syncing ? "Menyinkronkan..." : "Sinkronkan Order Pending"}
                </button>
            </div>

            <div className="border border-racing-line overflow-x-auto mb-8">
                <table className="w-full text-sm min-w-[760px]">
                    <thead className="bg-racing-surface border-b border-racing-line">
                        <tr>
                            {["Atlet", "Kelas", "Terkumpul", "Diredeem", "Saldo", "Aksi"].map((h) => <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-racing-muted">{h}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan="6" className="px-4 py-8 text-center text-racing-muted">Memuat data...</td></tr>}
                        {!loading && (!data || data.rows.length === 0) && (
                            <tr><td colSpan="6" className="px-4 py-8 text-center text-racing-muted" data-testid="funds-empty">Belum ada dukungan uang latihan yang masuk.</td></tr>
                        )}
                        {(data?.rows || []).map((row, i) => (
                            <tr key={row.athlete_id} className="border-b border-racing-line" data-testid={`funds-row-${i}`}>
                                <td className="px-4 py-3 font-medium">{row.name}</td>
                                <td className="px-4 py-3 text-racing-muted">{row.class_name}</td>
                                <td className="px-4 py-3">{rupiah(row.collected)}</td>
                                <td className="px-4 py-3 text-racing-muted">{rupiah(row.redeemed)}</td>
                                <td className="px-4 py-3 font-medium text-racing-red" data-testid={`funds-balance-${i}`}>{rupiah(row.balance)}</td>
                                <td className="px-4 py-3">
                                    <button onClick={() => openRedeem(row)} disabled={row.balance <= 0} data-testid={`fund-redeem-button-${i}`} className="bg-racing-red hover:bg-racing-redhover disabled:opacity-40 text-white text-[10px] uppercase tracking-[0.15em] px-3 py-2 transition-colors">
                                        Redeem
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                <div>
                    <h3 className="font-display uppercase text-lg mb-3">Dukungan Terbaru</h3>
                    <div className="border border-racing-line" data-testid="funds-recent">
                        {(data?.recent || []).length === 0 && <p className="px-4 py-6 text-sm text-racing-muted">Belum ada dukungan lunas.</p>}
                        {(data?.recent || []).map((f, i) => (
                            <div key={f.id} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-racing-line last:border-0 text-sm" data-testid={`fund-recent-${i}`}>
                                <div>
                                    <p className="font-medium">{f.athlete_name}</p>
                                    <p className="text-xs text-racing-muted">{f.supporter_name} · {(f.paid_at || f.created_at || "").slice(0, 10)}</p>
                                    {f.message && <p className="text-xs text-neutral-900/80 mt-1" data-testid={`fund-recent-message-${i}`}>&ldquo;{f.message}&rdquo;</p>}
                                </div>
                                <p className="font-medium text-racing-red">{rupiah(f.amount)}</p>
                            </div>
                        ))}
                    </div>
                </div>
                <div>
                    <h3 className="font-display uppercase text-lg mb-3">Riwayat Redeem</h3>
                    <div className="border border-racing-line" data-testid="funds-redeem-history">
                        {(data?.redeems || []).length === 0 && <p className="px-4 py-6 text-sm text-racing-muted">Belum ada redeem.</p>}
                        {(data?.redeems || []).map((r, i) => (
                            <div key={r.id} className="px-4 py-3 border-b border-racing-line last:border-0 text-sm" data-testid={`fund-redeem-history-${i}`}>
                                <p className="font-medium">{r.athlete_name} — {rupiah(r.gross)}</p>
                                <p className="text-xs text-racing-muted">
                                    {r.kind === "dues" ? `Iuran ${MONTH_LABEL(r.period_month)}` : "Uang tunai"} · potongan {rupiah(r.fee)} · diterima {rupiah(r.net)} · oleh {r.created_by} · {(r.created_at || "").slice(0, 10)}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <Dialog open={!!redeemForm} onOpenChange={(open) => { if (!open) setRedeemForm(null); }}>
                <DialogContent className="bg-white border border-racing-line text-neutral-900 max-w-md" data-testid="fund-redeem-dialog">
                    <DialogHeader><DialogTitle className="font-display uppercase text-2xl">Redeem — {redeemForm?.name}</DialogTitle></DialogHeader>
                    {redeemForm && (
                        <div className="mt-2 space-y-4">
                            <p className="text-sm text-racing-muted">Saldo tersedia: <span className="font-medium text-neutral-900">{rupiah(redeemForm.balance)}</span></p>
                            <div className="flex border border-racing-line" data-testid="redeem-kind-toggle">
                                <button onClick={() => setRedeemForm({ ...redeemForm, kind: "dues" })} data-testid="redeem-kind-dues" className={`flex-1 px-4 py-3 text-xs uppercase tracking-[0.15em] transition-colors ${redeemForm.kind === "dues" ? "bg-racing-red text-white" : "text-racing-muted"}`}>Iuran Bulanan</button>
                                <button onClick={() => setRedeemForm({ ...redeemForm, kind: "cash" })} data-testid="redeem-kind-cash" className={`flex-1 px-4 py-3 text-xs uppercase tracking-[0.15em] transition-colors ${redeemForm.kind === "cash" ? "bg-racing-red text-white" : "text-racing-muted"}`}>Uang Tunai</button>
                            </div>
                            {redeemForm.kind === "dues" && (
                                <div>
                                    <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Bulan Iuran Tujuan</label>
                                    <select value={redeemForm.period_month} onChange={(e) => setRedeemForm({ ...redeemForm, period_month: e.target.value })} data-testid="redeem-period-select" className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm">
                                        <option value="">Pilih bulan</option>
                                        {unpaidMonths.map((m) => <option key={m} value={m}>{MONTH_LABEL(m)}</option>)}
                                    </select>
                                </div>
                            )}
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Jumlah Redeem</label>
                                <input type="number" min="1" max={redeemForm.balance} value={redeemForm.amount} onChange={(e) => setRedeemForm({ ...redeemForm, amount: e.target.value })} data-testid="redeem-amount-input" className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm" />
                            </div>
                            <div className="border border-racing-line bg-racing-surface p-4 text-sm space-y-1" data-testid="redeem-summary">
                                <p className="flex justify-between"><span className="text-racing-muted">Potongan {redeemForm.kind === "dues" ? "(flat per redeem)" : "(admin 12%)"}</span><span>{rupiah(fee)}</span></p>
                                <p className="flex justify-between font-medium"><span>{redeemForm.kind === "dues" ? "Masuk sebagai iuran" : "Diterima tunai"}</span><span className="text-racing-red">{rupiah(net)}</span></p>
                            </div>
                            <div className="flex gap-3">
                                <button onClick={submitRedeem} disabled={redeeming} data-testid="redeem-submit" className="flex-1 bg-racing-red hover:bg-racing-redhover disabled:opacity-60 text-white text-xs uppercase tracking-[0.2em] py-3.5 transition-colors">
                                    {redeeming ? "Memproses..." : "Proses Redeem"}
                                </button>
                                <button type="button" onClick={() => setRedeemForm(null)} data-testid="redeem-cancel" className="flex-1 border border-racing-line text-racing-muted text-xs uppercase tracking-[0.2em] py-3.5">Batal</button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
